import React, { useState } from "react";
import moment from "moment";
import {
	calculateFromGear,
	calculateFromBody,
	calculateFromCenters,
	cubicInchesToCm,
	cubicCmToInches,
} from "./api/calculations";
import PDFDownloader from "./components/PDFDownloader";

const App = () => {
	//can be mm or inches
	const [units, setUnits] = useState("mm");
	//can be 'd', 'W' or 'c'
	const [dimension, setDimension] = useState("d");
	const [dimensionValue, setDimensionValue] = useState("");
	const [D, setD] = useState("");
	const [gW, setgW] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [result, setResult] = useState(0);

	const handleUnitsChange = (e) => {
		setUnits(e.target.value);
		resetResult();
		resetError();
	};

	const handleDimensionChange = (e) => {
		setDimension(e.target.value);
		resetResult();
		resetError();
		resetThirdDimension();
	};

	const ongWValueChange = (e) => {
		const tempgWValue = e.target.value;
		if (!tempgWValue || tempgWValue.match(/^\d{1,}(\.\d{0,3})?$/)) {
			setgW(tempgWValue);
		}
	};

	const onDValueChange = (e) => {
		const tempDValue = e.target.value;
		if (!tempDValue || tempDValue.match(/^\d{1,}(\.\d{0,3})?$/)) {
			setD(tempDValue);
		}
	};

	const onDimensionValueChange = (e) => {
		const tempDimensionValue = e.target.value;
		if (
			!tempDimensionValue ||
			tempDimensionValue.match(/^\d{1,}(\.\d{0,3})?$/)
		) {
			setDimensionValue(tempDimensionValue);
		}
	};

	const onDimensionValueSubmit = (e) => {
		e.preventDefault();
		validateAndCalculate();
	};

	const resetError = () => {
		setErrorMessage("");
	};

	const resetResult = () => {
		setResult(0);
	};

	const resetThirdDimension = () => {
		setDimensionValue("");
	};

	//returns an object wiht information about current calculation to print
	//in the PDF file by the PDFDownloader component
	const createDataObj = () => {
		return {
			D,
			gW,
			dimension,
			dimensionValue,
			units,
			resultString:
				units === "mm"
					? `${(result / 1000).toFixed(2)} cm³ (${cubicCmToInches(
							result / 1000
					  ).toFixed(3)} inches³)`
					: `${result.toFixed(3)} inches³ (${cubicInchesToCm(result).toFixed(
							2
					  )} cm³)`,
			created: moment().format("LLLL"),
		};
	};

	const validateAndCalculate = () => {
		resetError();
		//check if inputs are present
		if (!D || !gW || !dimensionValue) {
			setErrorMessage("Input fields must contain positive non-zero values");
			return;
		}
		//get numbers form strings, check for 0s
		const Dtemp = parseFloat(D);
		const gWtemp = parseFloat(gW);
		const dimtemp = parseFloat(dimensionValue);
		if (!Dtemp || !gWtemp || !dimtemp) {
			setErrorMessage("Input fields must contain positive non-zero values");
			return;
		}
		//check if dimensions make sense
		if (dimension === "d" && dimtemp >= Dtemp) {
			setErrorMessage(
				"Please, verify your input values, d must be smaller than D"
			);
			return;
		}
		if (dimension === "W" && (dimtemp >= 2 * Dtemp || dimtemp <= 1.5 * Dtemp)) {
			setErrorMessage(
				"Please, verify your input values, W must be smaller than 2D and bigger than 1.5D"
			);
			return;
		}
		if (dimension === "c" && (dimtemp >= Dtemp || dimtemp <= Dtemp / 2)) {
			setErrorMessage(
				"Please, verify your input values, c must be smaller than D, and bigger than D/2"
			);
			return;
		}
		//update form fields
		setD(Dtemp.toString());
		setgW(gWtemp.toString());
		setDimensionValue(dimtemp.toString());
		//update state with calculated results
		switch (dimension) {
			case "d":
				setResult(calculateFromGear(Dtemp, gWtemp, dimtemp).displacement);
				break;
			case "W":
				setResult(calculateFromBody(Dtemp, gWtemp, dimtemp).displacement);
				break;
			case "c":
				setResult(calculateFromCenters(Dtemp, gWtemp, dimtemp).displacement);
				break;
			default:
				setResult(0);
		}
	};

	return (
		<div>
			<h3 style={styles.headerStyle}>Select Units</h3>
			<div style={styles.radioButtonSection}>
				<label style={styles.label}>
					<input
						type="radio"
						value="mm"
						name="units"
						checked={units === "mm"}
						onChange={handleUnitsChange}
					/>
					mm
				</label>
				<label style={styles.label}>
					<input
						type="radio"
						value="inches"
						name="units"
						checked={units === "inches"}
						onChange={handleUnitsChange}
					/>
					inches
				</label>
			</div>

			<div>
				<h3 style={styles.headerStyle}>Enter Dimensions</h3>
				<form onSubmit={onDimensionValueSubmit} style={styles.linkContainer}>
					<div style={styles.inputDiv}>
						<label htmlFor="gW">{`gW (${units}) = `}</label>
						<input
							type="text"
							value={gW}
							placeholder={`gW (${units})`}
							onChange={ongWValueChange}
							onFocus={resetError}
							style={styles.inputText}
							id="gW"
						/>
					</div>
					<div style={styles.inputDiv}>
						<label htmlFor="D">{`D (${units}) = `}</label>
						<input
							type="text"
							value={D}
							placeholder={`D (${units})`}
							onChange={onDValueChange}
							onFocus={resetError}
							style={styles.inputText}
							id="D"
						/>
					</div>
					<div style={styles.radioButtonSection}>
						<label style={styles.label}>
							<input
								type="radio"
								value="d"
								name="dimension"
								checked={dimension === "d"}
								onChange={handleDimensionChange}
							/>
							d
						</label>
						<label style={styles.label}>
							<input
								type="radio"
								value="W"
								name="dimension"
								checked={dimension === "W"}
								onChange={handleDimensionChange}
							/>
							W
						</label>
						<label style={styles.label}>
							<input
								type="radio"
								value="c"
								name="dimension"
								checked={dimension === "c"}
								onChange={handleDimensionChange}
							/>
							c
						</label>
					</div>
					<div style={styles.inputDiv}>
						<label htmlFor="dimension">{`${dimension} (${units})= `}</label>
						<input
							type="text"
							value={dimensionValue}
							placeholder={`${dimension} (${units})`}
							onChange={onDimensionValueChange}
							onFocus={resetError}
							style={styles.inputText}
							id="dimension"
						/>
					</div>
					<div style={styles.linkContainer}>
						<input type="submit" value="Calculate" style={styles.buttonStyle}/>
					</div>
				</form>
			</div>
			{!errorMessage ? <h3 style={styles.headerStyle}>Result</h3> : null}
			<div style={styles.resultContainer}>
				{errorMessage ? (
					<h4 style={styles.errorStyle}>{errorMessage}</h4>
				) : units === "mm" ? (
					<p>{`${(result / 1000).toFixed(2)} cm³ (${cubicCmToInches(
						result / 1000
					).toFixed(3)} inches³)`}</p>
				) : (
					<p>{`${result.toFixed(3)} inches³ (${cubicInchesToCm(result).toFixed(
						2
					)} cm³)`}</p>
				)}
			</div>

			<div>{!!result && <PDFDownloader data={createDataObj()} />}</div>
		</div>
	);
};

const styles = {
	headerStyle: {
		textAlign: "center",
		margin: "10px",
	},
	errorStyle: {
		textAlign: "center",
		color: "red",
		margin: "10px",
	},
	linkContainer: {
		textAlign: "center",
		margin: "10px",
		fontSize: "20px",
	},
	resultContainer: {
		textAlign: "center",
		fontSize: "22px",
	},
	radioButtonSection: {
		textAlign: "center",
		fontSize: "20px",
		marginTop: "10px",
		marginBottom: "10px",
	},
	label: {
		marginRight: "10px",
		marginLeft: "10px",
	},
	inputText: {
		fontSize: "18px",
		marginTop: "7px",
		marginBottom: "7px",
		padding: "5px",
	},
	inputDiv: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		flexDirection: "column",
	},
	buttonStyle: {
		width: '40%',
		padding: '5px',
		fontSize: "18px"
	}
};

export default App;
